<template>
  <div class="advanced-slot-section">
    <div
      class="add-tabs"
      role="tablist"
    >
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        role="tab"
        class="add-tab"
        :class="{ 'add-tab--active': activeTab === tab.id }"
        :aria-selected="activeTab === tab.id"
        @click="activeTab = tab.id"
      >
        <span
          class="add-tab__icon"
          v-html="tab.iconSvg"
        />
        {{ tab.label }}
      </button>
    </div>

    <div
      v-show="activeTab === 'upload'"
      class="tab-panel"
      role="tabpanel"
    >
      <div
        ref="uploadZone"
        class="upload-zone"
        :class="{ 'upload-zone--active': isDragOver }"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <svg
          class="upload-zone__icon"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line
            x1="12"
            x2="12"
            y1="3"
            y2="15"
          />
        </svg>
        <div class="upload-zone__title">
          Drop image(s) here or
          <label class="upload-link">
            upload
            <input
              type="file"
              multiple
              accept="image/*"
              class="d-none"
              @change="onFileInput"
            >
          </label>
        </div>
        <div class="upload-zone__hint">
          or paste with ⌘V
        </div>
        <button
          type="button"
          class="browse-btn"
          @click="openSelectFolderModal"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2" />
          </svg>
          Browse file library
        </button>
      </div>
    </div>

    <div
      v-show="activeTab === 'product'"
      class="tab-panel"
      role="tabpanel"
    >
      <div class="product-card">
        <div class="select-wrap product-card__type">
          <select
            v-model="productType"
            class="ui-input ui-input--xs"
          >
            <option value="ASIN">
              ASIN
            </option>
            <option value="URL">
              URL
            </option>
          </select>
          <svg
            class="select-chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </div>

        <div class="product-fetch-row">
          <input
            v-model="productValue"
            type="text"
            class="ui-input ui-input--xs product-fetch-row__input"
            :placeholder="productType === 'URL' ? 'https://amazon.com/dp/B087CY8B2R' : 'B087CY8B2R'"
            :disabled="productFetching"
            @keydown.enter="fetchProduct"
          >
          <button
            type="button"
            class="btn-fetch"
            :disabled="productFetching || !productValue.trim()"
            @click="fetchProduct"
          >
            <b-spinner
              v-if="productFetching"
              small
            />
            <span v-else>Fetch</span>
          </button>
        </div>

        <p
          v-if="productError"
          class="product-error"
        >
          {{ productError }}
        </p>

        <template v-if="fetchedImages.length">
          <div class="product-stats">
            <span class="product-stats__text">
              Fetched {{ fetchedImages.length }} · Selected {{ selectedCount }}
            </span>
            <button
              type="button"
              class="product-stats__action"
              @click="toggleSelectAll"
            >
              {{ allSelected ? 'Deselect all' : 'Select all' }}
            </button>
          </div>

          <div class="product-grid">
            <button
              v-for="(img, i) in fetchedImages"
              :key="`fetched-${i}`"
              type="button"
              class="product-grid__item"
              :class="{ 'product-grid__item--selected': img.selected }"
              :title="`#${i + 1}`"
              @click="toggleImageSelection(i)"
            >
              <img
                :src="img.displayUrl"
                class="product-grid__img"
                :alt="`#${i + 1}`"
              >
              <div
                class="product-grid__check"
                :class="{ 'product-grid__check--selected': img.selected }"
              >
                ✓
              </div>
            </button>
          </div>
        </template>
      </div>
    </div>

    <div
      v-show="activeTab === 'describe'"
      class="tab-panel"
      role="tabpanel"
    >
      <div class="describe-card">
        <div class="describe-grid">
          <input
            v-model="slotName"
            type="text"
            class="ui-input describe-grid__name"
            placeholder="Name — e.g. Enhanced Hero Image"
          >

          <div class="select-wrap describe-grid__category">
            <select
              v-model="slotCategory"
              class="ui-input ui-input--sm"
            >
              <option
                v-for="category in categoryOptions"
                :key="category"
                :value="category"
              >
                {{ category }}
              </option>
            </select>
            <svg
              class="select-chevron"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </div>

          <div class="describe-grid__dimensions">
            <input
              v-model="slotWidth"
              type="number"
              class="ui-input ui-input--dim"
              placeholder="W"
            >
            <span class="dim-sep">×</span>
            <input
              v-model="slotHeight"
              type="number"
              class="ui-input ui-input--dim"
              placeholder="H"
            >
            <span class="dim-unit">px</span>
          </div>

          <textarea
            v-model="slotDescription"
            class="ui-textarea describe-grid__description"
            placeholder="Description — e.g. A 3D version of a pack shot, showing the product out of the box"
            rows="4"
          />

          <textarea
            v-model="slotAiDirection"
            class="ui-textarea describe-grid__ai"
            placeholder="Default AI direction (optional) — reusable prompt to seed generation"
            rows="4"
          />
        </div>

        <div class="describe-footer">
          <p class="describe-footer__hint">
            Also saved to your Image Types catalog.
          </p>
          <button
            type="button"
            class="btn-create-slot"
            :disabled="!slotName.trim()"
            @click="addDescribedSlot"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 12h14" />
              <path d="M12 5v14" />
            </svg>
            Create &amp; add slot
          </button>
        </div>
      </div>
    </div>

    <SelectFolderModal
      mode="default"
      :view-tabs="true"
      modal-title="Select images"
      @onSelectedItems="onFileBrowserItemsSelected"
    />
  </div>
</template>

<script>
import SelectFolderModal from "@frontend/components/modules/file-browser/modals/SelectFolderModal.vue";
import FileBrowser from '@frontend/services/api/file-browser';
import { IMAGE_FILE_TYPES } from '@frontend/components/modules/file-browser/constants';
import { FILE_TYPE } from '@frontend/constants/file-image-types';

const ASIN_PATTERN = /^B[A-Z0-9]{9}$/;

function resolveImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/share?file=')) {
    return url;
  }
  if (url.startsWith('/')) {
    return url;
  }
  return `/share?file=${url}`;
}

function getFileBrowserImageUrl(item) {
  if (item.path) return item.path;
  if (item.full_url) return item.full_url;
  if (item.original_path) return resolveImageUrl(item.original_path);
  return '';
}

function isImageFileItem(item) {
  if (!item || item.type === FILE_TYPE.FOLDER) return false;
  return IMAGE_FILE_TYPES.includes(item.type);
}

function extractAsin(value, productType) {
  const trimmed = (value || '').trim().toUpperCase();
  if (!trimmed) return '';

  if (productType === 'ASIN') {
    return trimmed;
  }

  const dpMatch = trimmed.match(/\/dp\/([A-Z0-9]{10})/i);
  if (dpMatch) {
    return dpMatch[1].toUpperCase();
  }

  const asinMatch = trimmed.match(/\bB[A-Z0-9]{9}\b/i);
  return asinMatch ? asinMatch[0].toUpperCase() : '';
}

const ICONS = {
  upload: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>',
  package: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/><path d="M12 22V12"/><path d="m3.3 7 7.703 4.734a2 2 0 0 0 1.994 0L20.7 7"/><path d="m7.5 4.27 9 5.15"/></svg>',
  sparkles: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/></svg>',
};

export default {
  name: 'AdvancedSlotAdding',
  components: {
    SelectFolderModal,
  },
  props: {
    initialTab: {
      type: String,
      default: 'upload',
    },
  },
  data() {
    return {
      activeTab: this.initialTab,
      isDragOver: false,
      dragDepth: 0,
      productType: 'ASIN',
      productValue: '',
      productFetching: false,
      productError: '',
      fetchedAsin: '',
      fetchedImages: [],
      slotName: '',
      slotDescription: '',
      slotAiDirection: '',
      slotCategory: 'PDP',
      slotWidth: '',
      slotHeight: '',
      referenceCounter: 1,
      categoryOptions: ['PDP', 'Email', 'Social', 'Display', 'In-store'],
      tabs: [
        { id: 'upload', label: 'Upload', iconSvg: ICONS.upload },
        { id: 'product', label: 'From product', iconSvg: ICONS.package },
        { id: 'describe', label: 'Describe new type', iconSvg: ICONS.sparkles },
      ],
    };
  },
  computed: {
    selectedCount() {
      return this.fetchedImages.filter((img) => img.selected).length;
    },
    allSelected() {
      return this.fetchedImages.length > 0 && this.selectedCount === this.fetchedImages.length;
    },
  },
  watch: {
    initialTab(tab) {
      if (tab) {
        this.activeTab = tab;
      }
    },
  },
  mounted() {
    window.addEventListener('paste', this.onPaste);
  },
  beforeDestroy() {
    window.removeEventListener('paste', this.onPaste);
  },
  methods: {
    onDragEnter(event) {
      this.dragDepth += 1;
      this.isDragOver = true;
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    onDragOver(event) {
      this.isDragOver = true;
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    onDragLeave(event) {
      this.dragDepth = Math.max(0, this.dragDepth - 1);

      const related = event.relatedTarget;
      const zone = this.$refs.uploadZone;
      if (this.dragDepth === 0 || (zone && related && !zone.contains(related))) {
        this.dragDepth = 0;
        this.isDragOver = false;
      }
    },
    onDrop(e) {
      this.dragDepth = 0;
      this.isDragOver = false;
      const files = Array.from(e.dataTransfer.files).filter((file) => file.type.startsWith('image/'));
      files.forEach((file) => this.addUploadedSlot(file));
    },
    onFileInput(e) {
      Array.from(e.target.files || []).forEach((file) => this.addUploadedSlot(file));
      e.target.value = '';
    },
    onPaste(e) {
      const items = Array.from(e.clipboardData?.items || []);
      items
        .filter((item) => item.type.startsWith('image/'))
        .forEach((item) => {
          const file = item.getAsFile();
          if (file) {
            this.addUploadedSlot(file);
          }
        });
    },
    addUploadedSlot(file) {
      const label = `Reference ${this.referenceCounter++}`;
      this.$emit('add-slot', {
        label,
        type: 'reference',
        imageUrl: URL.createObjectURL(file),
        file,
      });
    },
    async fetchProduct() {
      this.productError = '';
      const asin = extractAsin(this.productValue, this.productType);

      if (!ASIN_PATTERN.test(asin)) {
        this.productError = this.productType === 'URL'
          ? 'Enter a valid Amazon product URL containing an ASIN.'
          : 'Enter a valid 10-character ASIN (starts with B).';
        return;
      }

      this.productFetching = true;

      try {
        const { data } = await FileBrowser.fetchAsinImages(asin);
        const urls = Array.isArray(data?.images) ? data.images : [];

        if (!urls.length) {
          this.fetchedImages = [];
          this.fetchedAsin = asin;
          this.productError = data?.message || 'No images found for this ASIN.';
          return;
        }

        this.fetchedAsin = asin;
        this.fetchedImages = urls.map((url, index) => ({
          url,
          displayUrl: resolveImageUrl(url),
          label: `#${index + 1}`,
          selected: false,
        }));
      } catch (error) {
        this.fetchedImages = [];
        const response = error?.response?.data;
        this.productError = response?.message || response?.error || 'Failed to fetch images for this product.';
      } finally {
        this.productFetching = false;
      }
    },
    toggleImageSelection(index) {
      const image = this.fetchedImages[index];
      if (!image) return;

      const nextSelected = !image.selected;
      this.$set(this.fetchedImages, index, { ...image, selected: nextSelected });

      if (nextSelected) {
        this.$emit('add-slot', {
          label: image.label,
          type: 'product',
          imageUrl: image.displayUrl,
        });
      } else {
        this.$emit('remove-slot', {
          label: image.label,
          imageUrl: image.displayUrl,
        });
      }
    },
    toggleSelectAll() {
      const selectAll = !this.allSelected;

      this.fetchedImages.forEach((image, index) => {
        if (image.selected === selectAll) return;

        this.$set(this.fetchedImages, index, { ...image, selected: selectAll });

        if (selectAll) {
          this.$emit('add-slot', {
            label: image.label,
            type: 'product',
            imageUrl: image.displayUrl,
          });
        } else {
          this.$emit('remove-slot', {
            label: image.label,
            imageUrl: image.displayUrl,
          });
        }
      });
    },
    addDescribedSlot() {
      if (!this.slotName.trim()) return;

      this.$emit('add-slot', {
        label: this.slotName.trim(),
        description: this.slotDescription.trim(),
        aiDirection: this.slotAiDirection.trim(),
        category: this.slotCategory,
        width: this.slotWidth ? Number(this.slotWidth) : null,
        height: this.slotHeight ? Number(this.slotHeight) : null,
        type: 'custom',
      });

      this.slotName = '';
      this.slotDescription = '';
      this.slotAiDirection = '';
      this.slotCategory = 'PDP';
      this.slotWidth = '';
      this.slotHeight = '';
    },
    openSelectFolderModal() {
      this.$bvModal.show('select-folder-modal');
    },
    onFileBrowserItemsSelected(items) {
      if (!Array.isArray(items) || !items.length) return;

      const imageItems = items.filter(isImageFileItem);
      if (!imageItems.length) return;

      imageItems.forEach((item) => {
        const imageUrl = getFileBrowserImageUrl(item);
        if (!imageUrl) return;

        const label = item.name?.trim() || `Reference ${this.referenceCounter++}`;

        this.$emit('add-slot', {
          label,
          type: 'reference',
          imageUrl,
          originalPath: item.original_path || null,
        });
      });
    },
    focusTab(tabId) {
      this.activeTab = tabId;
    },
  },
};
</script>

<style scoped>
.advanced-slot-section {
  width: 100%;
}

.add-tabs {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 4px;
  border-radius: 6px;
  background: #f3f4f6;
  margin-bottom: 8px;
}

.add-tab {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, box-shadow 0.12s;
  white-space: nowrap;
}

.add-tab--active {
  background: #fff;
  color: #0f172a;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.add-tab__icon {
  display: inline-flex;
  align-items: center;
  line-height: 0;
}

.tab-panel {
  margin-top: 8px;
}

.upload-zone {
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  background-color: rgba(0, 0, 0, 0.02);
  padding: 24px 16px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.upload-zone:hover:not(.upload-zone--active) {
  border-color: rgba(26, 170, 138, 0.4);
}

.upload-zone--active {
  border: 2px dashed #1aaa8a;
  background-color: rgba(26, 170, 138, 0.06);
}

.upload-zone__icon {
  color: rgba(107, 114, 128, 0.6);
}

.upload-zone__title {
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
}

.upload-zone__hint {
  font-size: 11px;
  color: #6b7280;
}

.upload-link {
  color: #1aaa8a;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.browse-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  border: none;
  background: none;
  color: #1aaa8a;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
}

.browse-btn:hover {
  text-decoration: underline;
}

.product-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-card__type .ui-input--xs {
  height: 32px;
  font-size: 12px;
}

.product-fetch-row {
  display: flex;
  gap: 6px;
}

.product-fetch-row__input {
  flex: 1;
  min-width: 0;
}

.ui-input--xs {
  height: 32px;
  font-size: 12px;
}

.btn-fetch {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: #1aaa8a;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  min-width: 56px;
}

.btn-fetch:hover:not(:disabled) {
  background: #159475;
}

.btn-fetch:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.product-error {
  margin: 0;
  font-size: 12px;
  color: #ef4444;
}

.product-stats {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
}

.product-stats__text {
  font-size: 11px;
  color: #6b7280;
}

.product-stats__action {
  border: none;
  background: none;
  padding: 0;
  font-size: 11px;
  color: #1aaa8a;
  cursor: pointer;
}

.product-stats__action:hover {
  text-decoration: underline;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 4px;
  max-height: 140px;
  overflow-y: auto;
  padding-right: 2px;
}

.product-grid__item {
  position: relative;
  aspect-ratio: 1;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  padding: 0;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s;
}

.product-grid__item:hover {
  border-color: rgba(26, 170, 138, 0.5);
}

.product-grid__item--selected {
  border-color: #1aaa8a;
}

.product-grid__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.product-grid__check {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  border-radius: 2px;
  border: 1px solid #e5e7eb;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: transparent;
  transition: color 0.12s, border-color 0.12s, background 0.12s;
}

.product-grid__item:hover .product-grid__check {
  color: #6b7280;
}

.product-grid__check--selected {
  color: #1aaa8a;
  border-color: #1aaa8a;
  background: rgba(255, 255, 255, 0.95);
}

.ui-input,
.ui-textarea {
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font-size: 14px;
  padding: 8px 12px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.ui-input {
  height: 36px;
}

.ui-input--sm {
  height: 36px;
  font-size: 12px;
}

.ui-input--dim {
  height: 28px;
  font-size: 12px;
  border: none;
  box-shadow: none;
  padding: 0 4px;
  min-width: 0;
}

.ui-input:focus,
.ui-textarea:focus {
  outline: none;
  border-color: #1aaa8a;
  box-shadow: 0 0 0 2px rgba(26, 170, 138, 0.15);
}

.ui-input--dim:focus {
  box-shadow: none;
}

.ui-input::placeholder,
.ui-textarea::placeholder {
  color: #9ca3af;
}

.ui-textarea {
  min-height: 80px;
  resize: none;
  font-size: 12px;
  padding: 8px 12px;
}

.select-wrap {
  position: relative;
}

.select-wrap .ui-input {
  appearance: none;
  padding-right: 32px;
  cursor: pointer;
}

.select-chevron {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  opacity: 0.5;
}


.describe-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.describe-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 8px;
}

.describe-grid__name {
  grid-column: span 12;
}

.describe-grid__category {
  grid-column: span 12;
}

.describe-grid__dimensions {
  grid-column: span 12;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
}

.describe-grid__description,
.describe-grid__ai {
  grid-column: span 12;
}

.dim-sep {
  font-size: 12px;
  color: #6b7280;
}

.dim-unit {
  font-size: 10px;
  color: #6b7280;
  flex-shrink: 0;
}

.describe-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 4px;
}

.describe-footer__hint {
  font-size: 10px;
  color: #6b7280;
  margin: 0;
}

.btn-create-slot {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: #1aaa8a;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, opacity 0.15s;
}

.btn-create-slot:hover:not(:disabled) {
  background: #159475;
}

.btn-create-slot:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary-sm {
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 6px;
  background: #1aaa8a;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
}

.btn-primary-sm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (min-width: 640px) {
  .describe-grid__name {
    grid-column: span 6;
  }

  .describe-grid__category {
    grid-column: span 3;
  }

  .describe-grid__dimensions {
    grid-column: span 3;
  }

  .describe-grid__description {
    grid-column: span 6;
  }

  .describe-grid__ai {
    grid-column: span 6;
  }
}
</style>
